import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  portalAdminApi,
  type LeadTracking as LeadTrackingLevel,
  type PortalAdminState,
  type TeamChat,
  type TeamDossierItem,
  type WorkLogTeam,
} from '../../api/portalAdmin'
import { ApiError } from '../../lib/api'
import { formatDateBR, formatDateTimeBR } from '../../lib/format'
import { Spinner } from '../Spinner'
import { RequestList } from './PortalFeedback'

// Aba "Portal" da tela do cliente: o acesso do cliente ao portal (ClaudeCode/09) e o que
// ele vê. Ligar o portal e mexer em usuários: admin ou gerente principal (canManage).

const EVENT_LABEL: Record<string, string> = {
  login: 'entrou no portal',
  resposta: 'respondeu',
  evento: 'contou',
  publicacao: 'abriu',
  sair_da_lista: 'parou de receber o resumo semanal',
  foi_voce: 'respondeu "Foi você?"',
  nao_reconheco: 'NÃO reconhece alterações na conta',
  dossie: 'atualizou o que a IA sabe',
  pedido: 'pediu',
  chat: 'começou uma conversa no chat',
}

const errorText = (err: unknown) => (err instanceof ApiError || err instanceof Error ? err.message : 'Algo deu errado')
const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      {hint && <p className="mb-3 mt-0.5 text-xs text-slate-500">{hint}</p>}
      <div className={hint ? '' : 'mt-3'}>{children}</div>
    </section>
  )
}

export default function PortalAdmin({ clientId }: { clientId: string }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['portal-admin', clientId], queryFn: () => portalAdminApi.get(clientId) })
  const refresh = () => qc.invalidateQueries({ queryKey: ['portal-admin', clientId] })
  const toggle = useMutation({ mutationFn: (v: boolean) => portalAdminApi.setEnabled(clientId, v), onSuccess: refresh })

  if (q.isLoading) return <Spinner />
  if (q.error || !q.data) return <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorText(q.error)}</p>
  const s = q.data

  return (
    <div className="space-y-4">
      <Section title="Portal do cliente">
        <div className="space-y-3 text-sm">
          {!s.configured && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-800">
              Portal não configurado no servidor: faltam as variáveis PORTAL_URL e PORTAL_JWT_SECRET. Dá para cadastrar
              usuários, mas ninguém entra nem recebe e-mail até lá.
            </p>
          )}
          {!s.active && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-800">Cliente inativo: ninguém entra no portal e nada é enviado.</p>
          )}
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={s.enabled}
              disabled={!s.canManage || toggle.isPending}
              onChange={(e) => toggle.mutate(e.target.checked)}
              className="mt-0.5 rounded text-brand-600"
            />
            <span>
              <b className="font-medium text-slate-900">Portal ligado</b>
              <span className="block text-xs text-slate-500">
                Ligado: os usuários abaixo entram, recebem o resumo de segunda (9h) e as perguntas da semana. Desligado:
                ninguém entra e as sessões abertas caem.
                {!s.canManage && ' Só o admin ou o gerente principal do cliente mudam isto.'}
              </span>
            </span>
          </label>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
            {s.portalUrl && (
              <a href={s.portalUrl} target="_blank" rel="noreferrer" className="font-medium text-brand-700 hover:underline">
                {s.portalUrl.replace(/^https?:\/\//, '')} ↗
              </a>
            )}
            <span>{s.openQuestions} pergunta(s) esperando o cliente</span>
            <span>{s.openRequests} pedido(s) do cliente em aberto</span>
            <span>O painel que o cliente vê: aba Painel → "Ver como o cliente".</span>
          </div>
        </div>
      </Section>

      <Users clientId={clientId} s={s} onChange={refresh} />
      <LeadTracking clientId={clientId} s={s} onChange={refresh} />
      <ClientRequests clientId={clientId} />
      <Chats clientId={clientId} />
      <Publications clientId={clientId} s={s} onChange={refresh} />
      <Work clientId={clientId} />
      <Dossier clientId={clientId} />

      <Section title="Novidades do portal" hint="O que o cliente fez no portal: entrou, respondeu, contou, abriu.">
        {s.events.length === 0 ? (
          <p className="text-sm text-slate-500">Nada ainda.</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {s.events.map((e) => (
              <li key={e.id} className="flex gap-3">
                <span className="w-28 shrink-0 text-xs text-slate-400">{formatDateTimeBR(e.createdAt)}</span>
                <span className="text-slate-700">
                  <b className="font-medium">{e.userName ?? 'Cliente'}</b> {EVENT_LABEL[e.type] ?? e.type}
                  {e.text ? `: ${e.text}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  )
}

const LEAD_LEVELS: Array<{ level: LeadTrackingLevel | null; title: string; text: string }> = [
  {
    level: null,
    title: 'Ainda não sabemos',
    text: 'Pergunta dos contatos no geral e, uma vez, ao dono: "Quando chega um contato pelos anúncios, vocês conseguem saber de qual anúncio ele veio?". A resposta dele escolhe uma das opções abaixo.',
  },
  {
    level: 'campanha',
    title: 'Por campanha',
    text: 'O dono sabe de qual anúncio veio cada contato (CRM, etiqueta no WhatsApp). Pergunta como foram os contatos das 3 campanhas que mais trouxeram contatos e quantas vendas vieram dos anúncios.',
  },
  {
    level: 'geral',
    title: 'No geral',
    text: 'Sabe que o contato veio dos anúncios, mas não de qual. Pergunta como foram os contatos no geral e quantas vendas vieram dos anúncios.',
  },
  {
    level: 'nenhum',
    title: 'Só o movimento',
    text: 'Não separa os contatos. Pergunta só como foi o movimento da semana, comparado com uma semana normal.',
  },
]

const LEAD_BY: Record<string, string> = {
  dono: 'respondido pelo cliente',
  gerente: 'escolhido pela equipe',
  automatico: 'ajustado sozinho: 2 semanas só com "Não sei"',
}

function LeadTracking({ clientId, s, onChange }: { clientId: string; s: PortalAdminState; onChange: () => void }) {
  const set = useMutation({
    mutationFn: (level: LeadTrackingLevel | null) => portalAdminApi.setLeadTracking(clientId, level),
    onSuccess: onChange,
  })
  const { level, at, by } = s.leadTracking
  return (
    <Section
      title="Perguntas de segunda sobre os contatos"
      hint={'O que o cliente consegue responder sobre os contatos que chegam pelos anúncios. Duas semanas seguidas só com "Não sei" descem um nível sozinhas. Só para cliente de lead: e-commerce não recebe estas perguntas.'}
    >
      <div className="space-y-2 text-sm">
        {LEAD_LEVELS.map((o) => (
          <label key={o.level ?? 'null'} className="flex items-start gap-2">
            <input
              type="radio"
              name={`lead-tracking-${clientId}`}
              checked={level === o.level}
              disabled={set.isPending}
              onChange={() => set.mutate(o.level)}
              className="mt-0.5 text-brand-600"
            />
            <span>
              <b className="font-medium text-slate-900">{o.title}</b>
              <span className="block text-xs text-slate-500">{o.text}</span>
            </span>
          </label>
        ))}
        {level && at && (
          <p className="text-xs text-slate-500">
            Desde {formatDateBR(at)}{by && LEAD_BY[by] ? ` (${LEAD_BY[by]})` : ''}. Vale a partir da próxima segunda.
          </p>
        )}
        {set.error && <p className="text-xs text-red-700">{errorText(set.error)}</p>}
      </div>
    </Section>
  )
}

function Users({ clientId, s, onChange }: { clientId: string; s: PortalAdminState; onChange: () => void }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [invite, setInvite] = useState(true)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const add = useMutation({
    mutationFn: () => portalAdminApi.addUser(clientId, { name, email, invite }),
    onSuccess: (r) => {
      setName('')
      setEmail('')
      const inviteText = {
        sent: 'Convite enviado por e-mail.',
        not_enabled: 'Cadastrado. O convite sai quando o portal for ligado (use "Enviar link").',
        not_configured: 'Cadastrado, mas o portal não está configurado no servidor: o convite não saiu.',
        failed: `Cadastrado, mas o e-mail falhou: ${r.error ?? ''}`,
      }[r.invite]
      setMsg({ ok: r.invite === 'sent' || r.invite === 'not_enabled', text: inviteText })
      onChange()
    },
    onError: (err) => setMsg({ ok: false, text: errorText(err) }),
  })
  const act = useMutation({
    mutationFn: async (a: { kind: 'link' | 'revoke' | 'remove' | 'active' | 'weekly'; userId: string; value?: boolean }) => {
      if (a.kind === 'link') return portalAdminApi.sendLink(clientId, a.userId)
      if (a.kind === 'revoke') return portalAdminApi.revoke(clientId, a.userId)
      if (a.kind === 'remove') return portalAdminApi.removeUser(clientId, a.userId)
      if (a.kind === 'active') return portalAdminApi.updateUser(clientId, a.userId, { active: a.value })
      return portalAdminApi.updateUser(clientId, a.userId, { weeklyEmail: a.value })
    },
    onSuccess: (_r, a) => {
      setMsg({
        ok: true,
        text: { link: 'Link de entrada enviado.', revoke: 'Sessões encerradas.', remove: 'Usuário removido.', active: 'Atualizado.', weekly: 'Atualizado.' }[a.kind],
      })
      onChange()
    },
    onError: (err) => setMsg({ ok: false, text: errorText(err) }),
  })

  return (
    <Section title="Quem entra no portal" hint="Login sem senha: a pessoa recebe um link no e-mail, que vale 60 minutos e só funciona uma vez.">
      {s.users.length === 0 ? (
        <p className="mb-3 text-sm text-slate-500">Ninguém cadastrado ainda.</p>
      ) : (
        <div className="mb-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-xs text-slate-500">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">Nome</th>
                <th className="px-2 py-1.5 text-left font-medium">E-mail</th>
                <th className="px-2 py-1.5 text-left font-medium">Último acesso</th>
                <th className="px-2 py-1.5 text-left font-medium">Resumo semanal</th>
                {s.canManage && <th className="px-2 py-1.5 text-right font-medium">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {s.users.map((u) => (
                <tr key={u.id} className={`border-b border-slate-100 ${u.active ? '' : 'opacity-50'}`}>
                  <td className="px-2 py-1.5 text-slate-800">
                    {u.name}
                    {!u.active && <span className="ml-1 text-xs text-slate-400">(desativado)</span>}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600">{u.email}</td>
                  <td className="px-2 py-1.5 text-slate-500">{u.lastSeenAt ? formatDateTimeBR(u.lastSeenAt) : 'nunca'}</td>
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={u.weeklyEmail}
                      disabled={!s.canManage || act.isPending}
                      onChange={(e) => act.mutate({ kind: 'weekly', userId: u.id, value: e.target.checked })}
                      className="rounded text-brand-600"
                    />
                  </td>
                  {s.canManage && (
                    <td className="whitespace-nowrap px-2 py-1.5 text-right text-xs">
                      {u.active && (
                        <>
                          <button type="button" onClick={() => act.mutate({ kind: 'link', userId: u.id })} className="mr-2 font-medium text-brand-700 hover:underline">
                            Enviar link
                          </button>
                          <button type="button" onClick={() => act.mutate({ kind: 'revoke', userId: u.id })} className="mr-2 text-slate-600 hover:underline">
                            Derrubar sessões
                          </button>
                        </>
                      )}
                      <button type="button" onClick={() => act.mutate({ kind: 'active', userId: u.id, value: !u.active })} className="mr-2 text-slate-600 hover:underline">
                        {u.active ? 'Desativar' : 'Reativar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => window.confirm(`Remover ${u.name} do portal?`) && act.mutate({ kind: 'remove', userId: u.id })}
                        className="text-rose-600 hover:underline"
                      >
                        Remover
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {s.canManage && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setMsg(null)
            add.mutate()
          }}
          className="flex flex-wrap items-center gap-2"
        >
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome" className="rounded-md border border-slate-300 px-3 py-1.5 text-sm" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@cliente.com.br"
            className="w-64 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
          />
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} className="rounded text-brand-600" />
            mandar o convite agora
          </label>
          <button
            type="submit"
            disabled={add.isPending || !name.trim() || !email.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {add.isPending && <Spinner className="h-4 w-4 text-white" />}
            Dar acesso
          </button>
        </form>
      )}
      {msg && <p className={`mt-2 text-sm ${msg.ok ? 'text-emerald-700' : 'text-rose-700'}`}>{msg.text}</p>}
    </Section>
  )
}

function Publications({ clientId, s, onChange }: { clientId: string; s: PortalAdminState; onChange: () => void }) {
  const toggle = useMutation({
    mutationFn: (a: { id: string; hidden: boolean }) => portalAdminApi.setPublicationHidden(clientId, a.id, a.hidden),
    onSuccess: onChange,
  })
  return (
    <Section
      title="Publicado para o cliente"
      hint='Publique o quinzenal na tela do relatório (aba Quinzenal) e os achados no cartão da investigação ("Publicar para o cliente").'
    >
      {s.publications.length === 0 ? (
        <p className="text-sm text-slate-500">Nada publicado ainda.</p>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {s.publications.map((p) => (
            <li key={p.id} className={`flex flex-wrap items-center gap-2 ${p.hiddenAt ? 'opacity-50' : ''}`}>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{p.kind === 'achado' ? 'Achado' : 'Relatório'}</span>
              <span className="text-slate-800">{p.title}</span>
              <span className="text-xs text-slate-400">{formatDateBR(p.publishedAt)}</span>
              {p.hiddenAt && <span className="text-xs text-slate-500">(despublicado)</span>}
              <button
                type="button"
                onClick={() => toggle.mutate({ id: p.id, hidden: !p.hiddenAt })}
                className="ml-auto text-xs font-medium text-brand-700 hover:underline"
              >
                {p.hiddenAt ? 'Publicar de novo' : 'Despublicar'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

function Work({ clientId }: { clientId: string }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['portal-admin-work', clientId], queryFn: () => portalAdminApi.work(clientId) })
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null)
  const save = useMutation({
    mutationFn: (a: { kind: string; id: string; clientText?: string | null; clientHidden?: boolean }) =>
      portalAdminApi.updateWork(clientId, a.kind, a.id, { clientText: a.clientText, clientHidden: a.clientHidden }),
    onSuccess: () => {
      setEditing(null)
      qc.invalidateQueries({ queryKey: ['portal-admin-work', clientId] })
    },
  })
  const write = useMutation({
    mutationFn: () => portalAdminApi.writeTexts(clientId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-admin-work', clientId] }),
  })
  const work: WorkLogTeam | undefined = q.data?.work
  const generic = (work?.weeks ?? []).reduce(
    (n, w) => n + w.actions.filter((a) => !a.custom && !a.hidden && !a.otherAction).length,
    0
  )
  return (
    <Section
      title='"O que fizemos" — como o cliente vê'
      hint="Montado sozinho: análises da semana, ações marcadas como feitas e as alterações da agência na conta. A frase de cada ação é escrita pela IA (de madrugada e ao ligar o portal); edite ou esconda o que não deve aparecer."
    >
      {generic > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span>{generic} ação(ões) ainda com texto genérico.</span>
          <button
            type="button"
            disabled={write.isPending}
            onClick={() => write.mutate()}
            className="rounded-md border border-brand-300 px-2.5 py-1 font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Escrever frases agora
          </button>
          {write.isPending && <Spinner className="h-4 w-4" />}
          {write.data && <span className="text-emerald-700">{write.data.written} frase(s) escrita(s).</span>}
          {write.error && <span className="text-rose-700">{errorText(write.error)}</span>}
        </div>
      )}
      {q.isLoading && <Spinner />}
      {work && work.weeks.length === 0 && <p className="text-sm text-slate-500">Nada nas últimas 8 semanas.</p>}
      <div className="space-y-4">
        {work?.weeks.map((w) => (
          <div key={w.weekStart}>
            <div className="mb-1 text-sm font-medium text-slate-800">
              Semana de {ddmm(w.weekStart)} a {ddmm(w.weekEnd)}
            </div>
            <ul className="space-y-1 text-sm">
              {w.analyses.map((d) => (
                <li key={d} className="text-slate-600">
                  <span className="mr-2 text-xs text-slate-400">{ddmm(d)}</span>Análise semanal da conta
                </li>
              ))}
              {w.actions.map((a) => (
                <li key={a.id} className={a.hidden ? 'opacity-50' : ''}>
                  <span className="mr-2 text-xs text-slate-400">{ddmm(a.day)}</span>
                  {editing?.id === a.id ? (
                    <span className="inline-flex flex-wrap items-center gap-1">
                      <input
                        value={editing.text}
                        onChange={(e) => setEditing({ id: a.id, text: e.target.value })}
                        className="w-96 rounded border border-slate-300 px-2 py-0.5 text-sm"
                        maxLength={200}
                      />
                      <button type="button" onClick={() => save.mutate({ kind: a.kind, id: a.id, clientText: editing.text })} className="text-xs font-medium text-brand-700">
                        Salvar
                      </button>
                      <button type="button" onClick={() => setEditing(null)} className="text-xs text-slate-500">
                        Cancelar
                      </button>
                    </span>
                  ) : (
                    <>
                      <span className="text-slate-800">{a.text}</span>
                      {!a.custom && (
                        <span className="ml-1 text-xs text-slate-400">
                          {a.otherAction ? '(texto genérico — "Fiz outra ação": a IA não escreve, edite à mão)' : '(texto genérico)'}
                        </span>
                      )}
                      <button type="button" onClick={() => setEditing({ id: a.id, text: a.text })} className="ml-2 text-xs text-brand-700 hover:underline">
                        editar
                      </button>
                      <button
                        type="button"
                        onClick={() => save.mutate({ kind: a.kind, id: a.id, clientHidden: !a.hidden })}
                        className="ml-2 text-xs text-slate-500 hover:underline"
                      >
                        {a.hidden ? 'mostrar ao cliente' : 'esconder do cliente'}
                      </button>
                      <div className="ml-10 text-xs text-slate-400" title="Ação original (interna)">
                        {a.original}
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
            {w.changes.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {w.changes.map((c) => (
                  <span key={c.kind} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                    {c.text}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {save.error && <p className="mt-2 text-sm text-rose-700">{errorText(save.error)}</p>}
    </Section>
  )
}

// Conversas do chat "Perguntar à IA" do portal (decisão 10: a equipe vê tudo, só leitura).
function Chats({ clientId }: { clientId: string }) {
  const q = useQuery({ queryKey: ['portal-admin-chats', clientId], queryFn: () => portalAdminApi.chats(clientId) })
  const [open, setOpen] = useState<string | null>(null)
  const list = q.data?.conversations ?? []
  return (
    <Section
      title="Conversas no chat"
      hint={'O que o cliente perguntou em "Perguntar à IA" (últimos 30 dias). A IA só vê o que o portal mostra; pedidos e fatos que ela registrou aparecem marcados.'}
    >
      {q.isLoading ? (
        <Spinner />
      ) : list.length === 0 ? (
        <p className="text-sm text-slate-500">Nenhuma conversa ainda.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {list.map((c: TeamChat) => {
            const first = c.messages.find((m) => m.role === 'user')
            const actions = c.messages.flatMap((m) => m.meta?.actions ?? [])
            return (
              <li key={c.id} className="py-2">
                <button type="button" onClick={() => setOpen(open === c.id ? null : c.id)} className="flex w-full items-start gap-3 text-left">
                  <span className="w-28 shrink-0 text-xs text-slate-400">{formatDateTimeBR(c.updatedAt)}</span>
                  <span className="min-w-0 flex-1 text-sm text-slate-700">
                    <b className="font-medium">{c.user.name}</b>: {first?.content.slice(0, 140) ?? '—'}
                    <span className="ml-1 text-xs text-slate-400">
                      ({c.messages.filter((m) => m.role === 'user').length} pergunta(s){actions.length ? `, ${actions.length} registro(s)` : ''})
                    </span>
                  </span>
                </button>
                {open === c.id && (
                  <div className="mt-2 space-y-2 rounded-lg bg-slate-50 p-3">
                    {c.messages.map((m) => (
                      <div key={m.id} className="text-sm">
                        <span className={`text-xs font-semibold ${m.role === 'user' ? 'text-slate-700' : 'text-violet-700'}`}>
                          {m.role === 'user' ? c.user.name : 'IA'} · {formatDateTimeBR(m.createdAt)}
                        </span>
                        <p className="whitespace-pre-line text-slate-700">{m.content}</p>
                        {m.meta?.actions?.map((a, i) => (
                          <p key={i} className="text-xs text-emerald-700">
                            {a.type === 'pedido' ? 'Registrou pedido' : 'Registrou fato'}: {a.text}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Section>
  )
}

function ClientRequests({ clientId }: { clientId: string }) {
  const q = useQuery({ queryKey: ['portal-admin-requests', clientId], queryFn: () => portalAdminApi.clientRequests(clientId) })
  return (
    <Section title="Pedidos do cliente" hint="O que o cliente pediu pela página Pedidos do portal. A resposta aparece para ele lá.">
      {q.isLoading ? <Spinner /> : <RequestList requests={q.data?.requests ?? []} showClient={false} />}
    </Section>
  )
}

const VISIBILITY: Record<TeamDossierItem['visibility'], { label: string; cls: string }> = {
  shown: { label: 'o cliente vê', cls: 'bg-emerald-100 text-emerald-800' },
  internal: { label: 'interno', cls: 'bg-slate-200 text-slate-700' },
  unchecked: { label: 'ainda não classificado (não aparece)', cls: 'bg-amber-100 text-amber-800' },
}

function Dossier({ clientId }: { clientId: string }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['portal-admin-dossier', clientId], queryFn: () => portalAdminApi.dossier(clientId) })
  const refresh = () => qc.invalidateQueries({ queryKey: ['portal-admin-dossier', clientId] })
  const toggle = useMutation({
    mutationFn: (a: { id: string; internal: boolean }) => portalAdminApi.setInternal(clientId, a.id, a.internal),
    onSuccess: refresh,
  })
  const classify = useMutation({ mutationFn: () => portalAdminApi.classifyDossier(clientId), onSuccess: refresh })
  const items = q.data?.items ?? []
  const unchecked = items.filter((i) => i.visibility === 'unchecked').length
  return (
    <Section
      title='"Seu negócio" — o dossiê que o cliente revisa'
      hint='Fatos confirmados de economia, restrições, sazonalidade, público, ofertas e orçamento. O que é sobre o cliente ou a relação com a agência fica interno (a IA separa; você pode mudar). "Está errado" do cliente vira fato novo dele e aparece nas Novidades.'
    >
      {q.isLoading && <Spinner />}
      {unchecked > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span>{unchecked} fato(s) ainda sem classificação: não aparecem ao cliente.</span>
          <button
            type="button"
            disabled={classify.isPending}
            onClick={() => classify.mutate()}
            className="rounded-md border border-brand-300 px-2.5 py-1 font-semibold text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            Classificar agora
          </button>
          {classify.isPending && <Spinner className="h-4 w-4" />}
          {classify.error && <span className="text-rose-700">{errorText(classify.error)}</span>}
        </div>
      )}
      {q.data && items.length === 0 && <p className="text-sm text-slate-500">Nenhum fato confirmado nessas seções.</p>}
      <ul className="space-y-2 text-sm">
        {items.map((i) => (
          <li key={i.id} className={i.visibility === 'internal' ? 'opacity-60' : ''}>
            <span className="mr-2 text-xs text-slate-400">{i.section}</span>
            <span className="text-slate-800">{i.content}</span>
            <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-semibold ${VISIBILITY[i.visibility].cls}`}>
              {VISIBILITY[i.visibility].label}
            </span>
            {i.fromClient && <span className="ml-1 text-[11px] text-slate-500">· dito pelo cliente</span>}
            {i.clientReview === 'ok' && (
              <span className="ml-1 text-[11px] text-emerald-700">· conferido pelo cliente em {formatDateBR(i.clientReviewedAt)}</span>
            )}
            {!i.fromClient && (
              <button
                type="button"
                disabled={toggle.isPending}
                onClick={() => toggle.mutate({ id: i.id, internal: i.visibility !== 'internal' })}
                className="ml-2 text-xs text-brand-700 hover:underline"
              >
                {i.visibility === 'internal' ? 'mostrar ao cliente' : 'esconder (interno)'}
              </button>
            )}
          </li>
        ))}
      </ul>
      {toggle.error && <p className="mt-2 text-sm text-rose-700">{errorText(toggle.error)}</p>}
    </Section>
  )
}
